import React, { createContext, useContext, useState, useEffect } from 'react';
import { onAuthChange, getIdToken, signOutUser } from '../services/firebase';
import api from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authRequired, setAuthRequired] = useState(null); // null = checking
  const [accessDenied, setAccessDenied] = useState(false);
  const [deniedMessage, setDeniedMessage] = useState('');

  // Check if auth is required on mount
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const response = await api.get('/auth/status');
        setAuthRequired(response.data.authRequired);
      } catch (error) {
        console.error('Error checking auth status:', error);
        // Default to requiring auth if we can't check
        setAuthRequired(true);
      }
    };
    checkAuthStatus();
  }, []);

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthChange(async (firebaseUser) => {
      if (firebaseUser) {
        // User is signed in - check if they're allowed
        try {
          const token = await getIdToken();
          if (token) {
            // Set the token in axios defaults for subsequent requests
            api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            
            // Check if user is allowed
            const response = await api.get('/auth/check');
            
            if (response.data.allowed) {
              setUser({
                email: firebaseUser.email,
                displayName: firebaseUser.displayName,
                photoURL: firebaseUser.photoURL
              });
              setAccessDenied(false);
              setDeniedMessage('');
            } else {
              // User authenticated but not allowed
              setUser(null);
              setAccessDenied(true);
              setDeniedMessage(response.data.message);
              delete api.defaults.headers.common['Authorization'];
            }
          }
        } catch (error) {
          console.error('Auth check error:', error);
          setUser(null);
          setAccessDenied(true);
          setDeniedMessage('Authentication failed. Please try again.');
          delete api.defaults.headers.common['Authorization'];
        }
      } else {
        // User is signed out
        setUser(null);
        setAccessDenied(false);
        setDeniedMessage('');
        delete api.defaults.headers.common['Authorization'];
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Refresh token periodically (tokens expire after 1 hour)
  useEffect(() => {
    if (!user) return;

    const refreshToken = async () => {
      try {
        const token = await getIdToken();
        if (token) {
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        }
      } catch (error) {
        console.error('Token refresh error:', error);
      }
    };

    // Refresh every 55 minutes (before 1 hour expiry)
    const interval = setInterval(refreshToken, 55 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user]);

  const logout = async () => {
    try {
      await signOutUser();
      setUser(null);
      setAccessDenied(false);
      delete api.defaults.headers.common['Authorization'];
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const value = {
    user,
    loading,
    authRequired,
    accessDenied,
    deniedMessage,
    logout,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

